"use client";

import Image from "next/image";
import {
  Check,
  GraduationCap,
  Hammer,
  Link2,
  PawPrint,
  Quote,
  Truck,
} from "lucide-react";
import { useId, useState } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AeoSubpageCopy } from "./aeo-page-copy";

const TAB_ICONS = { Truck, Hammer, GraduationCap, PawPrint } as const;

/**
 * Layer9-style role tabs: pick an industry, see the question customers ask
 * and the AI answer that names the example business, with why it was chosen.
 */
export function CcAeoQuestions({
  copy,
}: {
  copy: AeoSubpageCopy["questions"];
}) {
  const [active, setActive] = useState(0);
  const baseId = useId();
  const tab = copy.tabs[active];
  const [before, after] = tab.answer.split(tab.brand);
  const engine = copy.engines[tab.engine] ?? copy.engines[0];

  return (
    <section
      className="cc-section cc-aeo-q"
      id="aeo-questions"
      aria-labelledby="cc-aeo-q-title"
      data-cc-nav="light"
      {...devSectionAttr("aeo-questions")}
    >
      <div className="cc-aeo-q__container">
        <header className="cc-aeo-head">
          <p className="cc-aeo-mono">{copy.eyebrow}</p>
          <h2 id="cc-aeo-q-title" className="cc-aeo-title">
            {copy.title}
          </h2>
          <p className="cc-aeo-intro">{copy.text}</p>
        </header>

        <div className="cc-aeo-q__board">
          <div
            className="cc-aeo-q__tabs"
            role="tablist"
            aria-label={copy.eyebrow}
          >
            {copy.tabs.map((item, index) => {
              const Icon =
                TAB_ICONS[item.icon as keyof typeof TAB_ICONS] ?? Truck;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`${baseId}-tab-${item.id}`}
                  aria-selected={index === active}
                  aria-controls={`${baseId}-panel`}
                  className="cc-aeo-q__tab"
                  onClick={() => setActive(index)}
                >
                  <span className="cc-aeo-q__tab-icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <span className="cc-aeo-q__tab-text">
                    <span className="cc-aeo-mono">{item.role}</span>
                    <strong>{item.label}</strong>
                  </span>
                </button>
              );
            })}
          </div>

          <div
            key={tab.id}
            className="cc-aeo-q__panel"
            role="tabpanel"
            id={`${baseId}-panel`}
            aria-labelledby={`${baseId}-tab-${tab.id}`}
          >
            <div className="cc-aeo-q__question">
              <Quote size={20} strokeWidth={2} aria-hidden="true" />
              <p>{tab.question}</p>
              <span className="cc-aeo-mono">{copy.whyLabel}</span>
              <ul>
                {tab.reasons.map((reason) => (
                  <li key={reason}>
                    <Check size={14} strokeWidth={2.6} aria-hidden="true" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
            <div className="cc-aeo-q__answer">
              <span className="cc-aeo-q__answer-head">
                <i aria-hidden="true">
                  <Image
                    src={engine.logo}
                    alt=""
                    width={16}
                    height={16}
                    unoptimized
                  />
                </i>
                {engine.name}
              </span>
              <p>
                {before}
                <mark>{tab.brand}</mark>
                {after}
              </p>
              <span className="cc-aeo-q__source">
                <Link2 size={13} strokeWidth={2.2} aria-hidden="true" />
                {tab.source}
              </span>
            </div>
          </div>
        </div>
        <p className="cc-aeo-q__note">{copy.exampleNote}</p>
      </div>
    </section>
  );
}
